import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Send, Loader2, Heart, User, Star, Upload, X, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Interactive star rating for submission form
const StarRating = ({ rating, setRating, readonly = false, size = 28 }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex items-center gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readonly && setRating(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`transition-transform ${!readonly && 'hover:scale-110 cursor-pointer'} ${readonly && 'cursor-default'}`}
          disabled={readonly}
          data-testid={`star-${star}`}
        >
          <Star
            size={size}
            className={`transition-colors ${
              (hover || rating) >= star
                ? 'fill-sip-yellow text-sip-yellow'
                : 'fill-gray-200 text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Display-only star rating showing average
const AverageStarRating = ({ rating, size = 16 }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = rating >= star;
        const partial = rating > star - 1 && rating < star;
        
        return (
          <Star
            key={star}
            size={size}
            className={`${
              filled 
                ? 'fill-sip-yellow text-sip-yellow' 
                : partial 
                  ? 'fill-sip-yellow/50 text-sip-yellow' 
                  : 'fill-gray-200 text-gray-300'
            }`}
          />
        );
      })}
    </div>
  );
};

// Community rating component with hover interaction
const CommunityRating = ({ flavorId, currentAverage, totalRatings, onRated }) => {
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  
  // Check localStorage to see if user already rated this
  useEffect(() => {
    const ratedFlavors = JSON.parse(localStorage.getItem('ratedFlavors') || '[]');
    setHasRated(ratedFlavors.includes(flavorId));
  }, [flavorId]);

  const handleRate = async (rating) => {
    if (hasRated || submitting) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/flavors/${flavorId}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating })
      });
      
      if (!response.ok) throw new Error('Failed to rate');
      
      const data = await response.json();
      
      // Save to localStorage to prevent re-rating
      const ratedFlavors = JSON.parse(localStorage.getItem('ratedFlavors') || '[]');
      ratedFlavors.push(flavorId);
      localStorage.setItem('ratedFlavors', JSON.stringify(ratedFlavors));
      
      setHasRated(true);
      onRated(flavorId, data.average_rating, data.total_ratings);
      toast.success('Thanks for rating!');
    } catch (error) {
      console.error('Rating error:', error);
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  if (hasRated) {
    return (
      <div className="flex items-center gap-2">
        <AverageStarRating rating={currentAverage} size={18} />
        <span className="text-xs text-gray-500">
          {currentAverage.toFixed(1)} ({totalRatings})
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div 
        className="flex items-center gap-0.5"
        onMouseLeave={() => setHover(0)}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleRate(star)}
            onMouseEnter={() => setHover(star)}
            disabled={submitting}
            className="transition-transform hover:scale-110 cursor-pointer disabled:opacity-50"
            data-testid={`rate-star-${star}`}
          >
            <Star
              size={18}
              className={`transition-colors ${
                hover >= star
                  ? 'fill-sip-pink text-sip-pink'
                  : currentAverage >= star
                    ? 'fill-sip-yellow text-sip-yellow'
                    : 'fill-gray-200 text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
      <span className="text-xs text-sip-purple font-medium">
        {hover > 0 ? 'Click to rate!' : `${currentAverage.toFixed(1)} (${totalRatings})`}
      </span>
    </div>
  );
};

export const FlavorFantasiaPage = () => {
  const [flavors, setFlavors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    flavor_combo: '',
    description: '',
    rating: 5,
    image_url: ''
  });
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFlavors();
  }, []);

  const fetchFlavors = async () => {
    try {
      const response = await fetch(`${API}/flavors`);
      const data = await response.json();
      setFlavors(data);
    } catch (error) {
      console.error('Failed to fetch flavors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingUpdate = (flavorId, newAverage, newTotal) => {
    setFlavors(flavors.map(f => 
      f.flavor_id === flavorId 
        ? { ...f, average_rating: newAverage, total_ratings: newTotal }
        : f
    ));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch(`${API}/public-upload`, {
        method: 'POST',
        body: uploadData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, image_url: data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image');
      setPreviewImage(null);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setPreviewImage(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/flavors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to submit');

      setSubmitted(true);
      setFormData({ name: '', flavor_combo: '', description: '', rating: 5, image_url: '' });
      setPreviewImage(null);
      toast.success('Your flavor idea has been submitted!');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white" data-testid="flavor-fantasia-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-blue via-sip-pink to-sip-yellow py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="font-script text-4xl md:text-6xl text-white mb-4">
              Share Your Flavor Fantasia
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Got a dream flavor combo? Share it with us and the community!
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Submit Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="font-script text-3xl text-sip-navy mb-6">
                Submit Your Flavor Idea
              </h2>
              
              {submitted ? (
                <div className="card-featured text-center py-8">
                  <Heart className="w-16 h-16 mx-auto mb-4 text-sip-pink" />
                  <h3 className="font-semibold text-xl text-gray-900 mb-2">
                    Thank You!
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Your flavor idea has been submitted! Once approved, it will appear in our community gallery.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-secondary"
                  >
                    Submit Another Idea
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="card-graffiti" data-testid="flavor-form">
                  <div className="space-y-5">
                    <div>
                      <label className="block font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="input-graffiti"
                        placeholder="Jane Doe"
                        data-testid="input-name"
                      />
                    </div>

                    <div>
                      <label className="block font-medium text-gray-700 mb-2">
                        Your Flavor Combination *
                      </label>
                      <Input
                        type="text"
                        name="flavor_combo"
                        value={formData.flavor_combo}
                        onChange={handleChange}
                        required
                        className="input-graffiti"
                        placeholder="e.g., Mango + Coconut + Lime Zest"
                        data-testid="input-flavor"
                      />
                    </div>

                    <div>
                      <label className="block font-medium text-gray-700 mb-2">
                        Rate Your Creation *
                      </label>
                      <div className="flex items-center gap-3">
                        <StarRating 
                          rating={formData.rating} 
                          setRating={(rating) => setFormData(prev => ({ ...prev, rating }))}
                        />
                        <span className="text-gray-500 text-sm">
                          {formData.rating} star{formData.rating !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-gray-700 mb-2">
                        Add a Photo (Optional)
                      </label>
                      
                      {previewImage ? (
                        <div className="relative inline-block">
                          <img 
                            src={previewImage} 
                            alt="Preview" 
                            className="w-full max-w-[200px] h-auto rounded-xl object-cover border-2 border-sip-purple/20"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            data-testid="remove-image-btn"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-sip-purple hover:bg-sip-purple/5 transition-colors"
                          data-testid="upload-area"
                        >
                          {uploading ? (
                            <Loader2 className="w-8 h-8 mx-auto text-sip-purple animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">
                                Click to upload an image
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                JPEG, PNG, GIF, WebP (Max 5MB)
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="file-input"
                      />
                    </div>

                    <div>
                      <label className="block font-medium text-gray-700 mb-2">
                        Tell Us More (Optional)
                      </label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="input-graffiti min-h-[100px]"
                        placeholder="What inspired this combo? Why do you love it?"
                        data-testid="input-description"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || uploading}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="submit-flavor-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Share My Flavor
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              <p className="text-sm text-gray-500 mt-4 text-center">
                Who knows? Your idea might become our next featured drink!
              </p>
            </motion.div>

            {/* Community Submissions */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="font-script text-3xl text-sip-navy mb-2">
                Community Favorites
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Rate your favorites by clicking the stars!
              </p>

              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-10 h-10 text-sip-blue animate-spin mx-auto" />
                </div>
              ) : flavors.length === 0 ? (
                <div className="card-graffiti text-center py-8">
                  <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    Be the first to share your flavor fantasia!
                  </p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[700px] overflow-y-auto pr-2">
                  {flavors.map((flavor, index) => (
                    <motion.div
                      key={flavor.flavor_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="card-graffiti"
                      data-testid={`flavor-card-${index}`}
                    >
                      {/* Image at top if exists */}
                      {flavor.image_url && (
                        <div className="mb-4 -mx-4 -mt-4">
                          <img 
                            src={flavor.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${flavor.image_url}` : flavor.image_url}
                            alt={flavor.flavor_combo}
                            className="w-full h-48 object-cover rounded-t-xl"
                          />
                        </div>
                      )}
                      
                      <div className="flex items-start gap-3">
                        <div className="bg-gradient-to-br from-sip-blue to-sip-pink w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-gray-900">{flavor.name}</p>
                              <p className="text-sip-navy font-semibold mt-1">
                                {flavor.flavor_combo}
                              </p>
                            </div>
                            <CommunityRating
                              flavorId={flavor.flavor_id}
                              currentAverage={flavor.average_rating || flavor.rating}
                              totalRatings={flavor.total_ratings || 1}
                              onRated={handleRatingUpdate}
                            />
                          </div>
                          {flavor.description && (
                            <p className="text-gray-600 text-sm mt-2">
                              "{flavor.description}"
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FlavorFantasiaPage;
